export const URL_PARSE_BASE = "https://journalparty.local";
export const MAX_NESTED_URL_DEPTH = 3;

export const SENSITIVE_URL_QUERY_PARAMS = new Set([
  "access_token",
  "auth_token",
  "checkout_session_id",
  "client_recovered_at",
  "code",
  "confirmation_token",
  "conversion_event_id",
  "email_otp",
  "hashed_token",
  "error",
  "error_description",
  "id_token",
  "magiclink",
  "otp",
  "password",
  "paypal_order_id",
  "payment_intent",
  "payment_intent_client_secret",
  "recovery_token",
  "refresh_token",
  "redirect_status",
  "sb",
  "session_id",
  "srsltid",
  "status",
  "subscription_id",
  "token",
  "token_hash",
  "transaction_id",
]);

const HIGH_RISK_AUTH_CREDENTIAL_PARAMS = new Set([
  "access_token",
  "auth_token",
  "confirmation_token",
  "email_otp",
  "hashed_token",
  "id_token",
  "magiclink",
  "otp",
  "password",
  "recovery_token",
  "refresh_token",
  "token",
  "token_hash",
]);

function canonicalParamName(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

const SENSITIVE_URL_QUERY_PARAM_CANONICALS = new Set(
  Array.from(SENSITIVE_URL_QUERY_PARAMS, canonicalParamName),
);

const HIGH_RISK_AUTH_CREDENTIAL_CANONICALS = new Set(
  Array.from(HIGH_RISK_AUTH_CREDENTIAL_PARAMS, canonicalParamName),
);

export function isHighRiskAuthCredentialParamName(value: string): boolean {
  const normalized = value.trim().toLowerCase();
  const canonical = canonicalParamName(normalized);
  return (
    HIGH_RISK_AUTH_CREDENTIAL_PARAMS.has(normalized) ||
    HIGH_RISK_AUTH_CREDENTIAL_CANONICALS.has(canonical) ||
    normalized.includes("password") ||
    normalized.includes("secret") ||
    canonical.endsWith("accesstoken") ||
    canonical.endsWith("authtoken") ||
    canonical.endsWith("confirmationtoken") ||
    canonical.endsWith("emailotp") ||
    canonical.endsWith("idtoken") ||
    canonical.endsWith("recoverytoken") ||
    canonical.endsWith("refreshtoken") ||
    canonical.endsWith("tokenhash")
  );
}

const AUTH_CREDENTIAL_PATHS = new Set([
  "/auth/callback",
  "/auth/confirm-email",
  "/auth/native-callback",
  "/auth/verify",
]);

declare global {
  interface Window {
    __JP_SENSITIVE_AUTH_TELEMETRY_SUPPRESSED__?: boolean;
  }
}

export const URL_SCHEME_PATTERN = /^[a-z][a-z0-9+.-]*:/i;

export function normalizedParamName(value: string): string {
  return value.trim().toLowerCase();
}

function containsControlCharacter(value: string): boolean {
  return Array.from(value).some((character) => {
    const codePoint = character.codePointAt(0) ?? 0;
    return codePoint <= 0x1f || codePoint === 0x7f;
  });
}

function isSensitiveParamName(value: string): boolean {
  const normalized = normalizedParamName(value);
  return (
    SENSITIVE_URL_QUERY_PARAMS.has(normalized) ||
    SENSITIVE_URL_QUERY_PARAM_CANONICALS.has(canonicalParamName(normalized)) ||
    isHighRiskAuthCredentialParamName(normalized) ||
    normalized.includes("secret") ||
    normalized.includes("password")
  );
}

/** `email`, `utm_email`, `user_email`: the serialized `key=` contains `email=`. */
function isEmailBearingQueryKey(key: string): boolean {
  return `${normalizedParamName(key)}=`.includes("email=");
}

function decodedQueryCandidates(value: string): string[] {
  const candidates = [value];
  let current = value;
  for (let attempt = 0; attempt < 2; attempt += 1) {
    try {
      const decoded = decodeURIComponent(current);
      if (decoded === current) break;
      current = decoded;
      candidates.push(decoded);
    } catch {
      break;
    }
  }
  return candidates;
}

/**
 * @, fullwidth ＠ (U+FF20), small commercial at ﹫ (U+FE6B),
 * %40 or %EF%BC%A0 with any run of 25s, or mailto: / mailto%3A.
 * Case-insensitive. A later broken % escape cannot hide the marker,
 * because the same pattern runs on the raw value and each decode.
 */
const RAW_ADDRESS_LIKE =
  /[@＠﹫]|%(?:25)*40|%(?:25)*ef%(?:25)*bc%(?:25)*a0|mailto(?::|%(?:25)*3a)/i;

/**
 * True when a query value is an address or a mailto link.
 * The raw value and up to two decodes each go through RAW_ADDRESS_LIKE.
 * Callers of sanitizeSensitiveUrl all get this, including every event's
 * track-event envelope url and PostHog before_send.
 */
export function isAddressLikeQueryValue(value: string): boolean {
  return decodedQueryCandidates(value).some((candidate) =>
    RAW_ADDRESS_LIKE.test(candidate.trim()),
  );
}

function isMailtoQueryValue(value: string): boolean {
  return decodedQueryCandidates(value).some((candidate) =>
    candidate.trim().toLowerCase().startsWith("mailto:"),
  );
}

function sanitizeQueryValue(value: string, depth: number): string | null {
  if (isMailtoQueryValue(value)) return null;

  let next = value;
  if (depth < MAX_NESTED_URL_DEPTH) {
    const nested = decodeNestedUrlCandidate(value);
    if (looksLikeNestedUrl(nested) && !isMailtoQueryValue(nested)) {
      next = sanitizeSensitiveUrl(nested, "preserve", depth + 1);
    }
  }

  if (isAddressLikeQueryValue(next)) return null;
  return next;
}

export function looksLikeNestedUrl(value: string): boolean {
  const trimmed = value.trim();
  return (
    trimmed.startsWith("/") ||
    trimmed.startsWith("?") ||
    trimmed.startsWith("#") ||
    URL_SCHEME_PATTERN.test(trimmed)
  );
}

export function decodeNestedUrlCandidate(value: string): string {
  let candidate = value.trim();
  for (let index = 0; index < 2 && !looksLikeNestedUrl(candidate); index += 1) {
    try {
      const decoded = decodeURIComponent(candidate);
      if (decoded === candidate) break;
      candidate = decoded;
    } catch {
      break;
    }
  }
  return candidate;
}

function sanitizeHash(hash: string, depth: number): string {
  const raw = hash.startsWith("#") ? hash.slice(1) : hash;
  if (!raw) return "";

  const nested = decodeNestedUrlCandidate(raw);
  if (looksLikeNestedUrl(nested) && depth < MAX_NESTED_URL_DEPTH) {
    return `#${sanitizeSensitiveUrl(nested, "preserve", depth + 1)}`;
  }

  if (raw.includes("=") || raw.includes("&")) {
    const params = new URLSearchParams(raw);
    sanitizeSearchParams(params, depth + 1);
    const sanitized = params.toString();
    return sanitized ? `#${sanitized}` : "";
  }

  return `#${raw}`;
}

function sanitizeSearchParams(
  searchParams: URLSearchParams,
  depth: number,
): void {
  const keys = [...new Set(Array.from(searchParams.keys()))];

  for (const key of keys) {
    if (isSensitiveParamName(key) || isEmailBearingQueryKey(key)) {
      searchParams.delete(key);
      continue;
    }

    const values = searchParams.getAll(key);
    const sanitizedValues: string[] = [];
    let changed = false;
    for (const value of values) {
      const cleaned = sanitizeQueryValue(value, depth);
      if (cleaned == null) {
        changed = true;
        continue;
      }
      if (cleaned !== value) changed = true;
      sanitizedValues.push(cleaned);
    }

    if (!changed) continue;
    searchParams.delete(key);
    sanitizedValues.forEach((value) => searchParams.append(key, value));
  }
}

export function sanitizeSensitiveUrl(
  rawUrl: string,
  output: "absolute" | "path" | "preserve" = "preserve",
  depth = 0,
): string {
  if (!rawUrl) return rawUrl;

  try {
    const isAbsolute = URL_SCHEME_PATTERN.test(rawUrl.trim());
    const url = new URL(rawUrl, URL_PARSE_BASE);
    sanitizeSearchParams(url.searchParams, depth);
    url.hash = sanitizeHash(url.hash, depth);

    if (output === "path") {
      return `${url.pathname}${url.search}${url.hash}`;
    }
    if (output === "absolute") {
      return url.toString();
    }
    if (!isAbsolute && url.origin === URL_PARSE_BASE) {
      return `${url.pathname}${url.search}${url.hash}`;
    }
    return url.toString();
  } catch {
    return output === "path" ? "/" : "";
  }
}

function searchParamsContainHighRiskCredential(
  searchParams: URLSearchParams,
  depth: number,
): boolean {
  for (const [key, value] of searchParams.entries()) {
    const normalized = normalizedParamName(key);
    if (isHighRiskAuthCredentialParamName(normalized)) {
      return true;
    }
    if (depth >= MAX_NESTED_URL_DEPTH) continue;

    const nested = decodeNestedUrlCandidate(value);
    if (
      looksLikeNestedUrl(nested) &&
      containsHighRiskAuthCredential(nested, depth + 1)
    ) {
      return true;
    }
  }
  return false;
}

export function containsHighRiskAuthCredential(
  rawUrl: string,
  depth = 0,
): boolean {
  if (!rawUrl || depth > MAX_NESTED_URL_DEPTH) return false;

  try {
    const url = new URL(rawUrl, URL_PARSE_BASE);
    if (searchParamsContainHighRiskCredential(url.searchParams, depth)) {
      return true;
    }

    const rawHash = url.hash.startsWith("#") ? url.hash.slice(1) : url.hash;
    if (!rawHash) return false;
    const nestedHash = decodeNestedUrlCandidate(rawHash);
    if (looksLikeNestedUrl(nestedHash)) {
      return containsHighRiskAuthCredential(nestedHash, depth + 1);
    }
    if (rawHash.includes("=") || rawHash.includes("&")) {
      return searchParamsContainHighRiskCredential(
        new URLSearchParams(rawHash),
        depth + 1,
      );
    }
    return false;
  } catch {
    return false;
  }
}

export function isCredentialBearingAuthUrl(rawUrl: string): boolean {
  if (!rawUrl) return false;
  try {
    const url = new URL(rawUrl, URL_PARSE_BASE);
    const path = (url.pathname.replace(/\/+$/, "") || "/").toLowerCase();
    return (
      AUTH_CREDENTIAL_PATHS.has(path) && containsHighRiskAuthCredential(rawUrl)
    );
  } catch {
    return false;
  }
}

export function isSensitiveAuthTelemetryUrl(rawUrl: string): boolean {
  if (!rawUrl) return false;
  try {
    const url = new URL(rawUrl, URL_PARSE_BASE);
    const path = (url.pathname.replace(/\/+$/, "") || "/").toLowerCase();
    return AUTH_CREDENTIAL_PATHS.has(path);
  } catch {
    return false;
  }
}

/**
 * Keep third-party telemetry disabled for the lifetime of a page that loaded
 * an auth credential. AuthCallback scrubs the address bar immediately, but a
 * one-time in-memory latch prevents idle SDKs from starting afterward.
 */
export function shouldSuppressSensitiveAuthTelemetry(rawUrl?: string): boolean {
  if (
    typeof window !== "undefined" &&
    window.__JP_SENSITIVE_AUTH_TELEMETRY_SUPPRESSED__ === true
  ) {
    return true;
  }

  const candidate =
    rawUrl ?? (typeof window !== "undefined" ? window.location.href : "");
  const shouldSuppress = isSensitiveAuthTelemetryUrl(candidate);
  if (shouldSuppress && typeof window !== "undefined") {
    window.__JP_SENSITIVE_AUTH_TELEMETRY_SUPPRESSED__ = true;
  }
  return shouldSuppress;
}

function sanitizeHighRiskCredentialSearchParams(
  searchParams: URLSearchParams,
  depth: number,
): void {
  const keys = [...new Set(Array.from(searchParams.keys()))];

  for (const key of keys) {
    if (isHighRiskAuthCredentialParamName(key)) {
      searchParams.delete(key);
      continue;
    }
    if (depth >= MAX_NESTED_URL_DEPTH) continue;

    const values = searchParams.getAll(key);
    const sanitizedValues = values.map((value) => {
      const nested = decodeNestedUrlCandidate(value);
      return looksLikeNestedUrl(nested)
        ? sanitizeHighRiskAuthCredentials(nested, "preserve", depth + 1)
        : value;
    });

    if (sanitizedValues.some((value, index) => value !== values[index])) {
      searchParams.delete(key);
      sanitizedValues.forEach((value) => searchParams.append(key, value));
    }
  }
}

function sanitizeHighRiskCredentialHash(hash: string, depth: number): string {
  const raw = hash.startsWith("#") ? hash.slice(1) : hash;
  if (!raw) return "";

  const nested = decodeNestedUrlCandidate(raw);
  if (looksLikeNestedUrl(nested) && depth < MAX_NESTED_URL_DEPTH) {
    return `#${sanitizeHighRiskAuthCredentials(nested, "preserve", depth + 1)}`;
  }

  if (raw.includes("=") || raw.includes("&")) {
    const params = new URLSearchParams(raw);
    sanitizeHighRiskCredentialSearchParams(params, depth + 1);
    const sanitized = params.toString();
    return sanitized ? `#${sanitized}` : "";
  }

  return `#${raw}`;
}

/**
 * Remove only auth credentials from a navigable URL. Commerce identifiers are
 * deliberately preserved because post-checkout screens use them for durable
 * conversion deduplication. Use sanitizeSensitiveUrl for telemetry instead.
 */
export function sanitizeHighRiskAuthCredentials(
  rawUrl: string,
  output: "absolute" | "path" | "preserve" = "preserve",
  depth = 0,
): string {
  if (!rawUrl) return rawUrl;

  try {
    const isAbsolute = URL_SCHEME_PATTERN.test(rawUrl.trim());
    const url = new URL(rawUrl, URL_PARSE_BASE);
    sanitizeHighRiskCredentialSearchParams(url.searchParams, depth);
    url.hash = sanitizeHighRiskCredentialHash(url.hash, depth);

    if (output === "path") {
      return `${url.pathname}${url.search}${url.hash}`;
    }
    if (output === "absolute") {
      return url.toString();
    }
    if (!isAbsolute && url.origin === URL_PARSE_BASE) {
      return `${url.pathname}${url.search}${url.hash}`;
    }
    return url.toString();
  } catch {
    return "";
  }
}

export function normalizeSafeSameOriginPath(
  value: string | null | undefined,
  fallback = "/home",
  trustedOrigin = "https://journalparty.com",
): string {
  if (typeof value !== "string") return fallback;
  const candidate = value.trim();
  if (!candidate || !candidate.startsWith("/")) return fallback;

  let decodedCandidate = candidate;
  for (let attempt = 0; attempt < 3; attempt += 1) {
    if (
      decodedCandidate.startsWith("//") ||
      decodedCandidate.includes("\\") ||
      containsControlCharacter(decodedCandidate)
    ) {
      return fallback;
    }
    try {
      const decoded = decodeURIComponent(decodedCandidate);
      if (decoded === decodedCandidate) break;
      decodedCandidate = decoded;
    } catch {
      return fallback;
    }
  }

  try {
    const trustedBase = new URL(trustedOrigin);
    const sanitized = sanitizeHighRiskAuthCredentials(candidate, "path");
    if (!sanitized) return fallback;
    const parsed = new URL(sanitized, trustedBase);
    if (parsed.origin !== trustedBase.origin) return fallback;
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return fallback;
  }
}

const TRACKING_URL_FIELD_PATTERN =
  /(?:^|_)(?:url|uri|location|path|redirect|redirect_to|next|referrer)(?:$|_)/i;
const EMBEDDED_AUTH_CREDENTIAL_PATTERN =
  /(?:^|[^a-z0-9])(?:access[_-]?token|auth[_-]?token|confirmation[_-]?token|email[_-]?otp|hashed[_-]?token|id[_-]?token|magiclink|otp|password|recovery[_-]?token|refresh[_-]?token|token|token[_-]?hash)=/i;
const MAX_ANALYTICS_PAYLOAD_DEPTH = 6;

function containsEmbeddedAuthCredentialAssignment(value: string): boolean {
  let candidate = value;
  for (let attempt = 0; attempt < 3; attempt += 1) {
    if (EMBEDDED_AUTH_CREDENTIAL_PATTERN.test(candidate)) return true;
    try {
      const decoded = decodeURIComponent(candidate);
      if (decoded === candidate) return false;
      candidate = decoded;
    } catch {
      return false;
    }
  }
  return EMBEDDED_AUTH_CREDENTIAL_PATTERN.test(candidate);
}

function sanitizeAnalyticsValue(
  value: unknown,
  fieldName: string,
  depth: number,
): unknown {
  if (typeof value === "string") {
    const containsCredential = containsEmbeddedAuthCredentialAssignment(value);
    if (!TRACKING_URL_FIELD_PATTERN.test(fieldName) && !containsCredential) {
      return value;
    }

    const sanitized = sanitizeSensitiveUrl(value, "preserve");
    return containsCredential &&
      containsEmbeddedAuthCredentialAssignment(sanitized)
      ? "[redacted-sensitive-auth-data]"
      : sanitized;
  }
  if (value === null || typeof value !== "object") return value;
  if (depth >= MAX_ANALYTICS_PAYLOAD_DEPTH) {
    return Array.isArray(value) ? [] : {};
  }
  if (Array.isArray(value)) {
    return value.map((item) =>
      sanitizeAnalyticsValue(item, fieldName, depth + 1),
    );
  }

  const result: Record<string, unknown> = {};
  for (const [key, entry] of Object.entries(value as Record<string, unknown>)) {
    if (isHighRiskAuthCredentialParamName(key)) continue;
    result[key] = sanitizeAnalyticsValue(entry, key, depth + 1);
  }
  return result;
}

export function sanitizeAnalyticsEventData(
  value: unknown,
): Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  return sanitizeAnalyticsValue(value, "", 0) as Record<string, unknown>;
}

/**
 * Event property that marks names and titles as official Journal Party
 * catalog content (programs, Journeys, Live shows, products we publish).
 * Without it, title, name and description properties are dropped, because
 * they may be a member's own program or Journey. The product analytics
 * property rules live in productAnalyticsPrivacy.ts.
 */
export const OFFICIAL_CONTENT_PROPERTY = "is_official_content";

/**
 * Pages written for adults who work with teens (and the printable they hand
 * out) load no ad tracking: no Meta, TikTok, Google Ads or AppsFlyer while one
 * of them is the current page. GA4 and PostHog stay, behind consent.
 */
export const AD_EXCLUDED_PATH_PREFIXES = [
  "/journal-prompts/for-teens",
  "/journal-prompts/for-students",
  "/printables",
] as const;

function normalizeAdExclusionPathname(pathOrUrl: string): string | null {
  const trimmed = pathOrUrl.trim();
  if (!trimmed) return null;
  let pathname: string;
  try {
    pathname = new URL(trimmed, URL_PARSE_BASE).pathname;
  } catch {
    return null;
  }
  try {
    pathname = decodeURIComponent(pathname);
  } catch {
    // keep the raw pathname
  }
  pathname = pathname.toLowerCase().replace(/\/{2,}/g, "/");
  if (pathname.length > 1) pathname = pathname.replace(/\/+$/, "");
  return pathname || "/";
}

export function isAdExcludedPath(pathOrUrl: string | null | undefined): boolean {
  if (!pathOrUrl) return false;
  const pathname = normalizeAdExclusionPathname(pathOrUrl);
  if (!pathname) return false;
  return AD_EXCLUDED_PATH_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

/** Whether the page currently shown (or the given URL) is ad-excluded. */
export function isCurrentPageAdExcluded(sourceUrl?: string | null): boolean {
  if (isAdExcludedPath(sourceUrl)) return true;
  if (typeof window === "undefined") return false;
  return isAdExcludedPath(window.location.pathname);
}
